import React, { useState, useEffect } from "react";
import { UpdatePasswordRequestDto } from "../../../../services/Dtos";
import { VStack } from "@chakra-ui/react";
import {
  validateConfirmPassword,
  validatePassword,
  validateRequired,
} from "../../../../services/validation";
import CustomAlert from "../../../CustomUi/CustomAlert";
import CustomModal from "../../../CustomUi/CustomModal";
import { Field } from "@chakra-ui/react";
import useIcons from "../../../../hooks/useIcons";
import { PasswordInput } from "../../../ui/password-input";
import useAuth from "../../../../hooks/useAuth";
import { useFormChanges } from "../../../../hooks/useFormChanges";
import { useFormFieldHandler } from "../../../../hooks/useFormFieldHandler";

interface ChangePasswordProps {
  isOpen: boolean;
  onClose: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordProps> = ({
  isOpen,
  onClose,
}) => {
  const initialFormState: UpdatePasswordRequestDto = {
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  };

  const icons = useIcons();
  const { updatePassword, loading } = useAuth();
  const [formState, setFormState] =
    useState<UpdatePasswordRequestDto>(initialFormState);
  const [apiError, setApiError] = useState<string | null>(null);
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  // Define field configurations for form validation
  const fieldConfigs = {
    currentPassword: { type: "text" as const, required: true },
    newPassword: { type: "text" as const, required: true },
    confirmNewPassword: { type: "text" as const, required: true },
  };

  // Use the form changes hook
  const { hasChanges, validateField, validateAllFields, hasValidationErrors } =
    useFormChanges({
      fields: fieldConfigs,
      initialData: null,
      currentData: formState,
      isEditMode: false,
    });

  // Use the form field handler hook
  const { handleFieldChange } = useFormFieldHandler({
    setFormState: setFormState,
    setTouchedFields,
  });

  // Custom validation for password confirmation
  const validatePasswordConfirmation = (value: string): string => {
    if (!validateRequired(value)) {
      return "Password confirmation is required";
    }
    if (!validateConfirmPassword(value, formState.newPassword)) {
      return "Passwords do not match";
    }
    return "";
  };

  // Custom validation for passwords
  const validatePasswordField = (value: string): string => {
    if (!validateRequired(value)) {
      return "Password is required";
    }
    if (!validatePassword(value)) {
      return "Password must contain at least one lowercase letter, one uppercase letter, one symbol, and be at least 8 characters long.";
    }
    return "";
  };

  // Real-time validation using the hook's validateField
  const validateFieldByName = (name: string, value: any): string => {
    const fieldKey = name as keyof UpdatePasswordRequestDto;

    // Custom validation for specific fields
    if (name === "currentPassword" || name === "newPassword") {
      return validatePasswordField(value);
    }
    if (name === "confirmNewPassword") {
      return validatePasswordConfirmation(value);
    }

    return validateField(fieldKey, value);
  };

  // Clear errors when modal opens
  useEffect(() => {
    if (isOpen) {
      setApiError(null);
      setTouchedFields(new Set());
    }
  }, [isOpen]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormState(initialFormState);
      setTouchedFields(new Set());
    }
  }, [isOpen]);

  const showFieldError = (fieldName: string): boolean => {
    const error = validateFieldByName(
      fieldName,
      formState[fieldName as keyof UpdatePasswordRequestDto]
    );
    return !!(error && touchedFields.has(fieldName));
  };

  const getFieldError = (fieldName: string): string => {
    return validateFieldByName(
      fieldName,
      formState[fieldName as keyof UpdatePasswordRequestDto]
    );
  };

  const currentValidationErrors = validateAllFields();
  const hasValidationErrorsValue = hasValidationErrors(currentValidationErrors);

  const handleChangePassword = async () => {
    setApiError(null);

    // Check if all required fields are filled
    if (!hasChanges) {
      return;
    }

    // Validate all fields
    const errors = validateAllFields();
    if (hasValidationErrors(errors)) {
      return;
    }

    try {
      const result = await updatePassword(formState);
      if (result?.succeeded) {
        setFormState(initialFormState);
        onClose();
      } else {
        setApiError(result?.message || "Failed to update password");
      }
    } catch (error) {
      setApiError("An unexpected error occurred");
    }
  };

  const handleCloseChangePasswordModal = () => {
    setFormState(initialFormState);
    setApiError(null);
    setTouchedFields(new Set());
    onClose();
  };

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={handleCloseChangePasswordModal}
      handleClose={handleCloseChangePasswordModal}
      handleAction={handleChangePassword}
      isLoading={loading}
      hasChanges={hasChanges}
      hasValidationErrors={hasValidationErrorsValue}
      isDisabled={!hasChanges || hasValidationErrorsValue || loading}
      title="Change Password"
      actionText="Done"
      icon={icons.utility.shield}
    >
      <VStack gap={4} alignItems="stretch">
        {apiError && <CustomAlert message={apiError} />}

        <Field.Root invalid={showFieldError("currentPassword")}>
          <Field.Label>Current Password</Field.Label>
          <PasswordInput
            type="password"
            name="currentPassword"
            value={formState.currentPassword}
            onChange={handleFieldChange}
          />
          <Field.ErrorText>
            {showFieldError("currentPassword")
              ? getFieldError("currentPassword")
              : ""}
          </Field.ErrorText>
        </Field.Root>

        <Field.Root invalid={showFieldError("newPassword")}>
          <Field.Label>New Password</Field.Label>
          <PasswordInput
            type="password"
            name="newPassword"
            value={formState.newPassword}
            onChange={handleFieldChange}
          />
          <Field.ErrorText>
            {showFieldError("newPassword") ? getFieldError("newPassword") : ""}
          </Field.ErrorText>
        </Field.Root>

        <Field.Root invalid={showFieldError("confirmNewPassword")}>
          <Field.Label>Confirm Password</Field.Label>
          <PasswordInput
            type="password"
            name="confirmNewPassword"
            value={formState.confirmNewPassword}
            onChange={handleFieldChange}
          />
          <Field.ErrorText>
            {showFieldError("confirmNewPassword")
              ? getFieldError("confirmNewPassword")
              : ""}
          </Field.ErrorText>
        </Field.Root>
      </VStack>
    </CustomModal>
  );
};

export default ChangePasswordModal;
